import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { get, post } from '../lib/apiClient';
import { requestFcmToken } from '../lib/firebase';

const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes
const INACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click'] as const;
import { STORAGE } from '@/_config';

const FCM_TOKEN_STORAGE_KEY = 'leadnova_fcm_token';

interface PlanFeatures {
  lead_credits: number | null;     // null = unlimited
  email_credits: number | null;    // null = unlimited
  ai_features: boolean;
  advanced_analytics: boolean;
  analytics_export: boolean;
  campaign_features: boolean;
}

interface Organization {
  id: string;
  name: string;
  plan: string;
  plan_status?: string | null;
  trial_ends_at?: string | null;
  plan_features?: PlanFeatures | null;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  company: string;
  plan: string;
  planStatus: string | null;
  role: string;
  organization: Organization | null;
  orgRole: 'owner' | 'admin' | 'member' | null;
  title: string | null;
  phone: string | null;
  timezone: string;
  avatarUrl: string | null;
  passwordChangedAt: string | null;
  securityAlertsEnabled: boolean;
}

interface ApiAuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    org_role?: string | null;
    organization: Organization | null;
    title?: string | null;
    phone?: string | null;
    timezone?: string | null;
    avatar_url?: string | null;
    password_changed_at?: string | null;
    security_alerts_enabled?: boolean;
  };
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
}

interface AuthContextValue {
  user: User | null;
  token: string | null;
  loginWithApiResponse: (response: ApiAuthResponse) => void;
  logout: () => void;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
}

const AUTH_STORAGE_KEY = STORAGE;

const AuthContext = createContext<AuthContextValue | null>(null);

function loadState(): AuthState {
  try {
    const saved = localStorage.getItem(AUTH_STORAGE_KEY);
    return saved ? JSON.parse(saved) : { user: null, token: null, refreshToken: null };
  } catch {
    return { user: null, token: null, refreshToken: null };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(loadState);
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = null;
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    inactivityTimer.current = setTimeout(() => {
      setState({ user: null, token: null, refreshToken: null });
    }, INACTIVITY_TIMEOUT_MS);
  }, [clearInactivityTimer]);

  // Start/stop inactivity tracking whenever auth state changes
  useEffect(() => {
    if (!state.user) {
      clearInactivityTimer();
      return;
    }
    INACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, resetInactivityTimer, true));
    resetInactivityTimer();
    return () => {
      INACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, resetInactivityTimer, true));
      clearInactivityTimer();
    };
  }, [!!state.user, resetInactivityTimer, clearInactivityTimer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist state changes to localStorage
  useEffect(() => {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  // Force logout when the apiClient detects an unrecoverable 401/422 auth failure.
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
      setState({ user: null, token: null, refreshToken: null });
    };
    window.addEventListener("auth:force-logout", handler);
    return () => window.removeEventListener("auth:force-logout", handler);
  }, []);

  // Register FCM device token after successful login (runs whenever user becomes authenticated).
  useEffect(() => {
    if (!state.token || !state.user) return;
    let cancelled = false;

    (async () => {
      const token = await requestFcmToken();
      if (cancelled || !token) return;
      localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
      try {
        await post('auth/device-token', { device_token: token, device_type: 'web' });
      } catch {
        // non-critical — push notifications will be unavailable but app still works
      }
    })();

    return () => { cancelled = true; };
  }, [!!state.token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep React state in sync when the apiClient refreshes tokens in the background.
  // Without this, any subsequent AuthContext state update would overwrite localStorage
  // with the old expired token, causing an infinite 401 → refresh → overwrite loop.
  useEffect(() => {
    const handler = (e: Event) => {
      const { token, refreshToken } = (e as CustomEvent<{ token: string; refreshToken: string }>).detail;
      setState((prev) => {
        if (!prev.user) return prev; // not logged in — ignore
        if (prev.token === token) return prev; // already up to date
        return { ...prev, token, refreshToken };
      });
    };
    window.addEventListener("auth:token-refreshed", handler);
    return () => window.removeEventListener("auth:token-refreshed", handler);
  }, []);

  const loginWithApiResponse = (response: ApiAuthResponse) => {
    const { user: apiUser, access_token, refresh_token } = response;
    const nameParts = (apiUser.name ?? '').split(' ');
    setState({
      token: access_token,
      refreshToken: refresh_token,
      user: {
        id: apiUser.id,
        email: apiUser.email,
        firstName: nameParts[0] ?? '',
        lastName: nameParts.slice(1).join(' '),
        fullName: apiUser.name ?? apiUser.email,
        company: apiUser.organization?.name ?? '',
        plan: apiUser.organization?.plan ?? 'trial',
        planStatus: apiUser.organization?.plan_status ?? null,
        role: apiUser.role,
        organization: apiUser.organization,
        orgRole: (apiUser.org_role as 'owner' | 'admin' | 'member' | null) ?? null,
        title: apiUser.title ?? null,
        phone: apiUser.phone ?? null,
        timezone: apiUser.timezone ?? 'UTC',
        avatarUrl: apiUser.avatar_url ?? null,
        passwordChangedAt: apiUser.password_changed_at ?? null,
        securityAlertsEnabled: apiUser.security_alerts_enabled ?? true,
      },
    });
  };

  const logout = () => {
    const fcmToken = localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
    localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
    // Fire-and-forget: revoke the backend session. The endpoint succeeds even
    // with an expired token, so no need to guard on token validity here.
    post('auth/logout', fcmToken ? { device_token: fcmToken } : {}).catch(() => {});
    setState({ user: null, token: null, refreshToken: null });
  };

  const refreshUser = useCallback(async () => {
    if (!state.token) return;
    try {
      const res = await get<{
        id: string;
        name: string;
        email: string;
        role: string;
        org_role?: string | null;
        organization: { id: string; name: string; plan: string; plan_status?: string | null; trial_ends_at?: string | null } | null;
        password_changed_at?: string | null;
        security_alerts_enabled?: boolean;
        title?: string | null;
        phone?: string | null;
        timezone?: string | null;
        avatar_url?: string | null;
      }>('auth/me');
      const apiUser = res.data;
      const nameParts = (apiUser.name ?? '').split(' ');
      setState((prev) => ({
        ...prev,
        user: {
          id: apiUser.id,
          email: apiUser.email,
          firstName: nameParts[0] ?? '',
          lastName: nameParts.slice(1).join(' '),
          fullName: apiUser.name ?? apiUser.email,
          company: apiUser.organization?.name ?? '',
          plan: apiUser.organization?.plan ?? 'trial',
          planStatus: apiUser.organization?.plan_status ?? null,
          role: apiUser.role,
          organization: apiUser.organization,
          orgRole: (apiUser.org_role as 'owner' | 'admin' | 'member' | null) ?? null,
          title: apiUser.title ?? null,
          phone: apiUser.phone ?? null,
          timezone: apiUser.timezone ?? 'UTC',
          avatarUrl: apiUser.avatar_url ?? null,
          passwordChangedAt: apiUser.password_changed_at ?? null,
          securityAlertsEnabled: apiUser.security_alerts_enabled ?? true,
        },
      }));
    } catch {
      // silently ignore — stale data is better than broken UI
    }
  }, [state.token]);

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        token: state.token,
        loginWithApiResponse,
        logout,
        isAuthenticated: !!state.user,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
